import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HorizontalScrollList from './HorizontalScrollList';
import { getPublicContests, getContestBannerUrl } from '../services/contestsApi';
import './ContestStrip.css';

const parseAsUTC = (dateStr) => {
  if (!dateStr) return null;
  const hasTZ = dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr);
  return new Date(hasTZ ? dateStr : dateStr + 'Z');
};

const getTimeLeft = (endDateStr) => {
  if (!endDateStr) return null;
  const end = parseAsUTC(endDateStr).getTime();
  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  const mins = Math.floor((diff % 3600000) / 60000);
  return `${hours}h ${mins}m left`;
};

const formatPrizeShort = (prizeConfig) => {
  if (!prizeConfig) return null;
  try {
    const p = JSON.parse(prizeConfig);
    return p.value || p.description || null;
  } catch {
    return prizeConfig;
  }
};

const STATUS_CONFIG = {
  REGISTRATION_OPEN: { label: 'Register Now', cls: 'cs-card__status--reg' },
  VOTING_OPEN: { label: 'Voting Open', cls: 'cs-card__status--vote' },
};

const ContestStripCard = ({ contest }) => {
  const statusCfg = STATUS_CONFIG[contest.status] || { label: contest.status, cls: '' };
  const endDate = contest.status === 'REGISTRATION_OPEN'
    ? contest.registrationEndDate
    : contest.votingEndDate;
  const timeLeft = getTimeLeft(endDate);
  const prize = formatPrizeShort(contest.prizeConfig);
  const bannerUrl = getContestBannerUrl(contest.bannerImageUrl);

  return (
    <Link to={`/contests/${contest.id}`} className="cs-card">
      <div className="cs-card__banner">
        {bannerUrl ? (
          <img src={bannerUrl} alt={contest.title} width="200" height="300" loading="lazy" />
        ) : (
          <div className="cs-card__banner-placeholder" />
        )}
        <span className={`cs-card__status ${statusCfg.cls}`}>{statusCfg.label}</span>
      </div>
      <div className="cs-card__body">
        <h3 className="cs-card__title">{contest.title}</h3>
        <div className="cs-card__meta">
          {contest.genre && <span className="cs-card__genre">{contest.genre}</span>}
          {timeLeft && <span className="cs-card__time">{timeLeft}</span>}
        </div>
        <div className="cs-card__footer">
          <span className="cs-card__entries">{contest.entryCount} {contest.entryCount === 1 ? 'entry' : 'entries'}</span>
          {prize && <span className="cs-card__prize">{prize}</span>}
        </div>
      </div>
    </Link>
  );
};

const ContestStrip = () => {
  const navigate = useNavigate();
  const [activeContests, setActiveContests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPublicContests()
      .then(data => {
        if (cancelled) return;
        const active = (data || []).filter(
          c => c.status === 'REGISTRATION_OPEN' || c.status === 'VOTING_OPEN'
        );
        setActiveContests(active);
      })
      .catch(() => {
        if (!cancelled) setActiveContests([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading || activeContests.length === 0) return null;

  return (
    <HorizontalScrollList
      title="Live"
      accentTitle="Contests"
      actionLabel="View All"
      onAction={() => navigate('/contests')}
    >
      {activeContests.map(contest => (
        <ContestStripCard key={contest.id} contest={contest} />
      ))}
    </HorizontalScrollList>
  );
};

export default ContestStrip;
